import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { type Vehiculo } from '../../../hooks/useSettings';
import ImageUploadField from './ImageUploadField';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const EMPTY_VEH: Omit<Vehiculo, 'id'> = {
  tipo: 'compra',
  nombre: '',
  precio: 0,
  precio_oferta: 0,
  oferta_activa: false,
  precio_dia: 0,
  descripcion: '',
  imagen: '',
  imagenes: [],
  km: 0,
  anio: new Date().getFullYear(),
  combustible: '',
  disponible: true,
  potencia: undefined,
  transmision: '',
  color: '',
  puertas: undefined,
  plazas: undefined,
};

type Subtab = 'compraventa' | 'alquiler';

/* ─── Multi-image gallery editor ─── */
const GaleriaEditor = ({
  imagenes,
  onChange,
}: {
  imagenes: string[];
  onChange: (imgs: string[]) => void;
}) => {
  const addImage = () => onChange([...imagenes, '']);
  const removeImage = (idx: number) => onChange(imagenes.filter((_, i) => i !== idx));
  const updateImage = (idx: number, val: string) =>
    onChange(imagenes.map((img, i) => (i === idx ? val : img)));

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
          Fotos adicionales ({imagenes.length})
        </label>
        <button
          type="button"
          onClick={addImage}
          className="flex items-center gap-1 px-2.5 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[10px] font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors border border-zinc-700"
        >
          <i className="ri-add-line" /> Añadir foto
        </button>
      </div>

      {imagenes.length === 0 ? (
        <div
          onClick={addImage}
          className="border border-dashed border-zinc-700 flex flex-col items-center justify-center py-6 text-zinc-600 cursor-pointer hover:border-zinc-500 hover:text-zinc-400 transition-colors"
        >
          <i className="ri-image-add-line text-2xl mb-1" />
          <p className="text-[10px] font-bold uppercase tracking-wider">
            Añadir más fotos del vehículo
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {imagenes.map((img, idx) => (
            <div key={idx} className="flex gap-2 items-start">
              <span className="flex-shrink-0 w-5 h-8 flex items-center justify-center text-zinc-600 text-[10px] font-bold">
                {idx + 2}
              </span>
              <div className="flex-1">
                <ImageUploadField
                  value={img}
                  onChange={(val) => updateImage(idx, val)}
                />
              </div>
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-zinc-600 hover:text-red-400 cursor-pointer transition-colors mt-0.5"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Main component ─── */
const VehiculosTab = () => {
  const [vehiculos, setVehiculos] = useState<Vehiculo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [subtab, setSubtab] = useState<Subtab>('compraventa');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('value')
        .eq('key', 'vehiculos')
        .maybeSingle();
      setVehiculos((data?.value as Vehiculo[]) ?? []);
      setLoading(false);
    };
    fetchData();
  }, []);

  const addVeh = () => {
    const tipo = subtab === 'alquiler' ? 'alquiler' : 'compra';
    const newId = `v-${Date.now()}`;
    setVehiculos((prev) => [...prev, { ...EMPTY_VEH, tipo, id: newId }]);
    setExpandedId(newId);
  };

  const removeVeh = (id: string) => {
    setVehiculos((prev) => prev.filter((v) => v.id !== id));
    if (expandedId === id) setExpandedId(null);
  };

  const updateVeh = <K extends keyof Vehiculo>(id: string, key: K, val: Vehiculo[K]) =>
    setVehiculos((prev) => prev.map((v) => (v.id === id ? { ...v, [key]: val } : v)));

  const handleSave = async () => {
    setSaving(true);
    await fetch(`${SUPABASE_URL}/functions/v1/bali-save-settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ updates: [{ key: 'vehiculos', value: vehiculos }] }),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const compraventaVehs = vehiculos.filter((v) => v.tipo !== 'alquiler');
  const alquilerVehs = vehiculos.filter((v) => v.tipo === 'alquiler');
  const activeList = subtab === 'alquiler' ? alquilerVehs : compraventaVehs;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider">
          {vehiculos.length} vehículo(s) total
        </h3>
        <div className="flex gap-2">
          <button
            onClick={addVeh}
            className="flex items-center gap-1.5 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-add-line" /> Añadir{' '}
            {subtab === 'alquiler' ? 'alquiler' : 'vehículo'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-black uppercase tracking-wider cursor-pointer whitespace-nowrap transition-all disabled:opacity-60 ${
              saved
                ? 'bg-green-500 text-white'
                : 'bg-yellow-400 hover:bg-yellow-300 text-black'
            }`}
          >
            <i
              className={`${
                saving
                  ? 'ri-loader-4-line animate-spin'
                  : saved
                  ? 'ri-check-line'
                  : 'ri-save-line'
              }`}
            />
            {saving ? 'Guardando…' : saved ? 'Guardado!' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-0 mb-6 border-b border-zinc-800">
        <button
          onClick={() => setSubtab('compraventa')}
          className={`flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all cursor-pointer whitespace-nowrap -mb-px ${
            subtab === 'compraventa'
              ? 'border-yellow-400 text-yellow-400'
              : 'border-transparent text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <i className="ri-car-line" />
          Compraventa / Motos
          <span className="ml-1 bg-zinc-800 text-zinc-400 text-[10px] px-1.5 py-0.5 rounded-full">
            {compraventaVehs.length}
          </span>
        </button>
        <button
          onClick={() => setSubtab('alquiler')}
          className={`flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all cursor-pointer whitespace-nowrap -mb-px ${
            subtab === 'alquiler'
              ? 'border-yellow-400 text-yellow-400'
              : 'border-transparent text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <i className="ri-key-line" />
          Coches de Alquiler
          <span className="ml-1 bg-zinc-800 text-zinc-400 text-[10px] px-1.5 py-0.5 rounded-full">
            {alquilerVehs.length}
          </span>
        </button>
      </div>

      {activeList.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-800 flex flex-col items-center justify-center py-16 text-zinc-600">
          <i
            className={`${subtab === 'alquiler' ? 'ri-key-line' : 'ri-car-line'} text-4xl mb-3`}
          />
          <p className="text-sm">
            Sin {subtab === 'alquiler' ? 'coches de alquiler' : 'vehículos'}. Pulsa
            &quot;Añadir&quot; para empezar.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeList.map((v) => {
            const isExpanded = expandedId === v.id;
            const totalImgs = 1 + (v.imagenes?.filter(Boolean).length ?? 0);

            return (
              <div key={v.id} className="bg-zinc-900 border border-zinc-800">
                {/* Collapsed header */}
                <div
                  className="flex items-center gap-3 p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : v.id)}
                >
                  {/* Thumb */}
                  <div className="w-14 h-10 flex-shrink-0 bg-zinc-800 overflow-hidden border border-zinc-700">
                    {v.imagen ? (
                      <img
                        src={v.imagen}
                        alt=""
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-600">
                        <i className="ri-car-line text-lg" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-bold truncate">
                      {v.nombre || <span className="text-zinc-600 italic">Sin nombre</span>}
                    </p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-zinc-500 text-xs capitalize">{v.tipo}</span>
                      {v.anio && (
                        <span className="text-zinc-600 text-xs">{v.anio}</span>
                      )}
                      {v.km !== undefined && (
                        <span className="text-zinc-600 text-xs">
                          {v.km.toLocaleString('es-ES')} km
                        </span>
                      )}
                      {totalImgs > 1 && (
                        <span className="flex items-center gap-1 text-zinc-500 text-[10px]">
                          <i className="ri-image-line" /> {totalImgs} fotos
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    {v.tipo !== 'alquiler' ? (
                      <span className="text-yellow-400 font-black text-sm">
                        {v.oferta_activa && v.precio_oferta
                          ? `${Number(v.precio_oferta).toLocaleString('es-ES')} €`
                          : `${Number(v.precio).toLocaleString('es-ES')} €`}
                      </span>
                    ) : (
                      <span className="text-yellow-400 font-black text-sm">
                        {v.precio_dia ?? '—'} €/día
                      </span>
                    )}

                    <div
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        v.disponible ? 'bg-green-500' : 'bg-zinc-600'
                      }`}
                      title={v.disponible ? 'Disponible' : 'No disponible'}
                    />

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeVeh(v.id);
                      }}
                      className="w-7 h-7 flex items-center justify-center text-zinc-600 hover:text-red-400 cursor-pointer transition-colors"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>

                    {isExpanded
                      ? <i className="ri-arrow-up-s-line text-zinc-500 text-lg" />
                      : <i className="ri-arrow-down-s-line text-zinc-500 text-lg" />
                    }
                  </div>
                </div>

                {/* Expanded form */}
                {isExpanded && (
                  <div className="border-t border-zinc-800 p-5">
                    {/* Type + disponible */}
                    <div className="flex items-center gap-4 mb-4 flex-wrap">
                      {subtab === 'compraventa' && (
                        <div className="flex gap-1 bg-zinc-800 p-1">
                          {(['compra', 'moto'] as const).map((t) => (
                            <button
                              key={t}
                              onClick={() => updateVeh(v.id, 'tipo', t)}
                              className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap transition-colors ${
                                v.tipo === t
                                  ? 'bg-yellow-400 text-black'
                                  : 'text-zinc-400 hover:text-white'
                              }`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      )}
                      {subtab === 'alquiler' && (
                        <span className="flex items-center gap-1.5 px-3 py-1 bg-yellow-400/10 text-yellow-400 text-[10px] font-bold uppercase tracking-wider border border-yellow-400/20">
                          <i className="ri-key-line" /> Alquiler
                        </span>
                      )}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <div
                          onClick={() => updateVeh(v.id, 'disponible', !v.disponible)}
                          className={`w-9 h-5 rounded-full transition-colors relative ${
                            v.disponible ? 'bg-yellow-400' : 'bg-zinc-700'
                          }`}
                        >
                          <div
                            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
                              v.disponible ? 'left-4' : 'left-0.5'
                            }`}
                          />
                        </div>
                        <span className="text-xs text-zinc-400">Disponible</span>
                      </label>
                    </div>

                    {/* Basic fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                      {(
                        [
                          { key: 'nombre', label: 'Nombre *', type: 'text', span: 2 },
                          ...(subtab !== 'alquiler'
                            ? [{ key: 'precio', label: 'Precio (€)', type: 'number', span: 1 }]
                            : []),
                          { key: 'combustible', label: 'Combustible', type: 'text', span: 1 },
                          { key: 'km', label: 'Kilómetros', type: 'number', span: 1 },
                          { key: 'anio', label: 'Año', type: 'number', span: 1 },
                          { key: 'potencia', label: 'Potencia (CV)', type: 'number', span: 1 },
                          { key: 'transmision', label: 'Transmisión', type: 'text', span: 1 },
                          { key: 'color', label: 'Color', type: 'text', span: 1 },
                          { key: 'puertas', label: 'Puertas', type: 'number', span: 1 },
                          { key: 'plazas', label: 'Plazas', type: 'number', span: 1 },
                        ] as { key: string; label: string; type: string; span: number }[]
                      ).map((f) => (
                        <div
                          key={f.key}
                          className={f.span === 2 ? 'sm:col-span-2 lg:col-span-2' : ''}
                        >
                          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
                            {f.label}
                          </label>
                          <input
                            type={f.type}
                            value={String((v as Record<string, unknown>)[f.key] ?? '')}
                            onChange={(e) =>
                              updateVeh(
                                v.id,
                                f.key as keyof Vehiculo,
                                f.type === 'number'
                                  ? e.target.value === ''
                                    ? undefined
                                    : Number(e.target.value)
                                  : (e.target.value as Vehiculo[keyof Vehiculo]),
                              )
                            }
                            className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 outline-none focus:border-yellow-400/50 transition-colors"
                          />
                        </div>
                      ))}

                      {subtab === 'alquiler' && (
                        <div>
                          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
                            Precio / día (€) *
                          </label>
                          <input
                            type="number"
                            value={v.precio_dia ?? 0}
                            onChange={(e) =>
                              updateVeh(v.id, 'precio_dia', Number(e.target.value))
                            }
                            className="w-full bg-zinc-800 border border-yellow-400/30 text-yellow-400 text-sm px-3 py-2 outline-none focus:border-yellow-400/60 transition-colors font-bold"
                          />
                        </div>
                      )}
                    </div>

                    {/* Main image */}
                    <ImageUploadField
                      label="Foto principal (portada)"
                      value={v.imagen}
                      onChange={(val) => updateVeh(v.id, 'imagen', val)}
                    />

                    {/* Gallery */}
                    <GaleriaEditor
                      imagenes={v.imagenes ?? []}
                      onChange={(imgs) => updateVeh(v.id, 'imagenes', imgs)}
                    />

                    {/* Oferta (solo compraventa) */}
                    {subtab === 'compraventa' && (
                      <div className="mt-3 flex items-center gap-4 py-3 border-t border-zinc-800 flex-wrap">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <div
                            onClick={() =>
                              updateVeh(v.id, 'oferta_activa', !v.oferta_activa)
                            }
                            className={`w-9 h-5 rounded-full transition-colors relative ${
                              v.oferta_activa ? 'bg-red-500' : 'bg-zinc-700'
                            }`}
                          >
                            <div
                              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
                                v.oferta_activa ? 'left-4' : 'left-0.5'
                              }`}
                            />
                          </div>
                          <span className="text-xs text-zinc-400 font-bold uppercase tracking-wider">
                            Activar oferta
                          </span>
                        </label>
                        {v.oferta_activa && (
                          <div className="flex items-center gap-2 flex-1">
                            <span className="text-zinc-600 text-xs whitespace-nowrap">
                              Precio oferta (€):
                            </span>
                            <input
                              type="number"
                              value={v.precio_oferta ?? 0}
                              onChange={(e) =>
                                updateVeh(v.id, 'precio_oferta', Number(e.target.value))
                              }
                              className="w-32 bg-zinc-800 border border-red-500/40 text-red-400 text-sm px-3 py-1.5 outline-none focus:border-red-400/60 transition-colors font-bold"
                              placeholder="0"
                            />
                            <span className="text-zinc-600 text-xs">
                              {v.precio > 0 && v.precio_oferta && v.precio_oferta > 0
                                ? `(${Math.round(
                                    (1 - v.precio_oferta / v.precio) * 100,
                                  )}% dto.)`
                                : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    <div className="mt-3">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
                        Descripción
                      </label>
                      <textarea
                        rows={3}
                        maxLength={500}
                        value={v.descripcion}
                        onChange={(e) => updateVeh(v.id, 'descripcion', e.target.value)}
                        className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 outline-none focus:border-yellow-400/50 transition-colors resize-none"
                      />
                      <p className="text-zinc-700 text-[10px] text-right mt-0.5">
                        {v.descripcion.length}/500
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VehiculosTab;
